<script setup>
/**
 * Tags.
 *
 * Archetype E: the page shell owns the title and the primary action, the body
 * carries the description, a search box, the count, and the table. The old
 * layout stacked a bespoke header on top of the table and hid the whole table
 * behind a full-page empty state; the table's own empty message says the same
 * thing without taking the search box away with it.
 */
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { picoSearch } from '@scmmishra/pico-search';

import AddLabel from './AddLabel.vue';
import EditLabel from './EditLabel.vue';
import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSearchInput from 'dashboard/components-next/Settings/SettingsSearchInput.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmDialog from 'dashboard/components-next/dialog/ConfirmDialog.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedLabel = ref({});
const searchQuery = ref('');

const records = computed(() => getters['labels/getLabels'].value);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, [
    { name: 'title', weight: 4 },
    'description',
  ]);
});
const uiFlags = computed(() => getters['labels/getUIFlags'].value);

const deleteMessage = computed(() => ` ${selectedLabel.value.title}?`);

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  selectedLabel.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedLabel.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteLabel = async id => {
  try {
    await store.dispatch('labels/delete', id);
    useAlert(t('LABEL_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('LABEL_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[selectedLabel.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedLabel.value.id] = true;
  closeDeletePopup();
  deleteLabel(selectedLabel.value.id);
};

const tableHeaders = computed(() => {
  return [
    t('LABEL_MGMT.LIST.TABLE_HEADER.NAME'),
    t('LABEL_MGMT.LIST.TABLE_HEADER.DESCRIPTION'),
    t('LABEL_MGMT.LIST.TABLE_HEADER.COLOR'),
    t('LABEL_MGMT.LIST.TABLE_HEADER.ACTION'),
  ];
});

onBeforeMount(() => {
  store.dispatch('labels/get');
});
</script>

<template>
  <SettingsPageShell
    wide
    :title="$t('LABEL_MGMT.HEADER')"
    :description="$t('LABEL_MGMT.DESCRIPTION')"
  >
    <template #actions>
      <Button
        :label="$t('LABEL_MGMT.HEADER_BTN_TXT')"
        size="sm"
        @click="openAddPopup"
      />
    </template>

    <template #tabs>
      <div class="flex items-center justify-between gap-3">
        <SettingsSearchInput
          v-model="searchQuery"
          :placeholder="$t('LABEL_MGMT.SEARCH_PLACEHOLDER')"
        />
        <span v-if="records.length" class="text-sm text-n-slate-11">
          {{ $t('LABEL_MGMT.COUNT', { n: records.length }) }}
        </span>
      </div>
    </template>

    <BaseTable
      :headers="tableHeaders"
      :items="filteredRecords"
      :loading="uiFlags.isFetching"
      :no-data-message="
        searchQuery ? $t('LABEL_MGMT.NO_RESULTS') : $t('LABEL_MGMT.LIST.404')
      "
    >
      <template #row="{ items }">
        <BaseTableRow v-for="label in items" :key="label.title" :item="label">
          <template #default>
            <BaseTableCell>
              <span class="text-body-main text-n-slate-12">
                {{ label.title }}
              </span>
            </BaseTableCell>

            <BaseTableCell>
              <span class="text-body-main text-n-slate-11">
                {{ label.description }}
              </span>
            </BaseTableCell>

            <BaseTableCell>
              <div class="flex items-center">
                <span
                  class="w-4 h-4 ltr:mr-2 rtl:ml-2 border border-solid rounded border-n-weak"
                  :style="{ backgroundColor: label.color }"
                />
                <span class="text-body-main text-n-slate-12">
                  {{ label.color }}
                </span>
              </div>
            </BaseTableCell>

            <BaseTableCell align="end">
              <div class="flex gap-3 justify-end flex-shrink-0">
                <Button
                  v-tooltip.top="$t('LABEL_MGMT.FORM.EDIT')"
                  icon="i-woot-edit-pen"
                  slate
                  sm
                  :is-loading="loading[label.id]"
                  @click="openEditPopup(label)"
                />
                <Button
                  v-tooltip.top="$t('LABEL_MGMT.FORM.DELETE')"
                  icon="i-woot-bin"
                  slate
                  sm
                  class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                  :is-loading="loading[label.id]"
                  @click="openDeletePopup(label)"
                />
              </div>
            </BaseTableCell>
          </template>
        </BaseTableRow>
      </template>
    </BaseTable>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddLabel @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditLabel :selected-response="selectedLabel" @close="hideEditPopup" />
    </woot-modal>

    <ConfirmDialog
      v-model:show="showDeleteConfirmationPopup"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :description="$t('LABEL_MGMT.DELETE.CONFIRM.MESSAGE')"
      :description-value="deleteMessage"
      :confirm-button-label="$t('LABEL_MGMT.DELETE.CONFIRM.YES')"
      :cancel-button-label="$t('LABEL_MGMT.DELETE.CONFIRM.NO')"
      @close="closeDeletePopup"
      @confirm="confirmDeletion"
    />
  </SettingsPageShell>
</template>
